import React, { useContext } from 'react';
import './ItemDetailView.css';
import '../../../XXX/GameSimulator/GameContainer2/GameGrid/ChampionCard/StatsDisplay/StatsDisplay.css';
import GameAssetsContext from '../../../XXX/context/GameAssetContext';
import RecipeTree from './RecipeTree';
import { ItemDetails } from '../../../XXX/GameSimulator/GameContainer2/GameGrid/ChampionCard/SettingsPanel/ItemShopPanel2';

interface Props {
  itemId: number;
  itemName: string;
  apiVersion?: string;
  onClose: () => void;
  className?: string;
}

// Map stat keys to their icon class names (from ItemShopPanel2)
const STAT_ICON_MAP: Record<string, string> = {
  'attackDamage': 'attack-damage-icon',
  'abilityPower': 'ability-power-icon',
  'armor': 'armor-icon',
  'magicResistance': 'magic-resist-icon',
  'attackSpeed': 'attack-speed-icon',
  'abilityHaste': 'ability-haste-icon',
  'health': 'health-icon',
  'mana': 'resource-icon',
  'movementSpeed': 'movement-speed-icon',
  'criticalStrikeChance': 'crit-chance-icon',
  'criticalStrikeDamage': 'crit-damage-icon',
  'critDamage': 'crit-chance-icon',
  'lifeSteal': 'life-steal-icon',
  'omnivamp': 'omnivamp-icon',
  'armorPenetrationPercent': 'armor-penetration-icon',
  'lethality': 'armor-penetration-icon',
  'magicPenetration': 'magic-penetration-icon',
  'magicPenetrationPercent': 'magic-penetration-icon',
  'tenacity': 'tenacity-icon',
  'baseHealthRegenPercent': 'health-regen-icon',
  'baseManaRegenPercent': 'resource-regen-icon',
  'baseHealthRegen': 'health-regen-icon',
  'baseManaRegen': 'resource-regen-icon',
  'healAndShieldPower': 'heal-shield-power-icon',
  'gold': 'gold-icon',
};

// Function to get a display name for a stat key (from ItemShopPanel2)
const getStatDisplayName = (statKey: string): string => {
  const displayNameMap: Record<string, string> = {
    'attackDamage': 'Attack Damage',
    'abilityPower': 'Ability Power',
    'armor': 'Armor',
    'magicResistance': 'Magic Resist',
    'attackSpeed': 'Attack Speed',
    'abilityHaste': 'Ability Haste',
    'health': 'Health',
    'mana': 'Mana',
    'movementSpeed': 'Movement Speed',
    'criticalStrikeChance': 'Critical Strike',
    'critDamage': 'Critical Damage',
    'lifeSteal': 'Life Steal',
    'omnivamp': 'Omnivamp',
    'armorPenetrationPercent': 'Armor Penetration (%)',
    'lethality': 'Lethality',
    'magicPenetration': 'Magic Penetration',
    'magicPenetrationPercent': 'Magic Penetration (%)',
    'tenacity': 'Tenacity',
    'healthRegen': 'Health Regen',
    'manaRegen': 'Mana Regen'
  };
  
  return displayNameMap[statKey] || statKey;
};

const ItemDetailView: React.FC<Props> = ({ 
  itemId, 
  itemName, 
  apiVersion = "15.9.1",
  onClose,
  className = ''
}) => {
  const { gameWideAssets } = useContext(GameAssetsContext);

  // Helper function to get item image URL
  const getItemImageUrl = (itemId: number) => {
    return `https://ddragon.leagueoflegends.com/cdn/${apiVersion}/img/item/${itemId}.png`;
  };

  // Get item details from gameWideAssets
  const getItemDetails = (itemId: number): ItemDetails | null => {
    if (!gameWideAssets?.itemsDetails) return null;
    return gameWideAssets.itemsDetails.find(item => 
      item.id === itemId.toString() || parseInt(item.id) === itemId
    ) || null;
  };

  const itemDetails = getItemDetails(itemId);

  if (!itemDetails) {
    return (
      <div className={`item-detail-view ${className}`}>
        <div className="item-detail-header">
          <h4>Item Details</h4>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        <div className="item-detail-content">
          <div className="no-item-data">
            No item data available for {itemName} (ID: {itemId})
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`item-detail-view ${className}`}>
      <div className="item-detail-header">
        <h4>Item Details</h4>
        <button className="close-button" onClick={onClose}>×</button>
      </div>
      
      <div className="item-detail-content">
        {/* Left side - Item Information */}
        <div className="item-info-section">
          <div className="item-header">
            <img 
              src={getItemImageUrl(itemId)} 
              alt={itemDetails.name}
              className="item-image-detail-view"
            />
            <div className="item-header-text">
              <div className="item-name">{itemDetails.name}</div>
              <div className="item-cost">
                <div className="stat-icon gold-icon"></div>
                {itemDetails.cost}
              </div>
            </div>
          </div>
          
          {/* Stats section */}
          {itemDetails.stats && Object.keys(itemDetails.stats).length > 0 && (
            <div className="item-stats">
              <h5>Stats</h5>
              <div className="stats-list">
                {Object.entries(itemDetails.stats).map(([key, value]) => (
                  <div key={key} className="stat-row">
                    <div className={`stat-icon ${STAT_ICON_MAP[key] || 'default-stat-icon'}`}></div>
                    <div className="stat-value">{value}</div>
                    <div className="stat-name">{getStatDisplayName(key)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Passives section */}
          {itemDetails.passives && itemDetails.passives.length > 0 && (
            <div className="item-passives">
              <h5>Passives</h5>
              <div className="passives-list">
                {itemDetails.passives.map((passive, index) => (
                  <div key={index} className="passive-item">
                    <div className="passive-name">{passive.name}</div>
                    <div className="passive-description">{passive.description}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        {/* Right side - Recipe Tree */}
        <div className="item-recipe-section">
          <h5>Recipe Tree</h5>
          <div className="recipe-tree-wrapper">
            <RecipeTree 
              itemId={itemId}
              apiVersion={apiVersion}
              className="item-detail-recipe-tree"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ItemDetailView;
